import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

/**
 * What the health funds publish about their own complementary-medicine offering.
 *
 * This exists to keep competitive claims defensible. Every number here is the
 * provider's own, and every row shows the URL and the capture date beside it —
 * a claim backed by that is a documented fact; the same claim improvised is an
 * allegation. The source line is deliberately impossible to miss.
 *
 * The column to read is "מהטיפול ה-21". Both funds lead with a large discount
 * and cap it at 20 sessions a year with the intake counted among them, so this
 * is what the same session costs once the cap is spent.
 */

interface Offering {
  id: number;
  fund: string;
  treatment: string;
  durationMinutes: number | null;
  durationNote: string | null;
  priceList: number | null;
  priceMember: number | null;
  pricePremium: number | null;
  priceTop: number | null;
  priceAfterQuota: number | null;
  quotaJumpPct: number | null;
  sourceUrl: string;
  capturedAt: string;
  captureMethod: string;
}

interface FundSummary {
  fund: string;
  offerings: number;
  annualQuota: number | null;
  quotaIncludesIntake: boolean;
  medianQuotaJumpPct: number | null;
  maxQuotaJumpPct: number | null;
  minDuration: number | null;
  maxDuration: number | null;
  capturedAt: string | null;
  captureMethod: string | null;
  sourceUrl: string | null;
  terms: string | null;
}

const money = (n: number | null) => (n === null ? "—" : `${Math.round(n)}₪`);

export function OfferingsPage() {
  const [fund, setFund] = useState<string>("all");

  const { data, isLoading, error } = useQuery<{ rows: Offering[]; funds: FundSummary[] }>({
    queryKey: ["entity-offerings"],
    queryFn: async () => {
      const r = await fetch("/api/entity-offerings", { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Could not load: {String(error)}</div>;

  const funds = data?.funds ?? [];
  const rows = (data?.rows ?? []).filter((r) => fund === "all" || r.fund === fund);

  return (
    <div className="space-y-6 p-6" dir="rtl">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">מה קופות החולים מציעות</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          הקטלוג, המחירים והתנאים שהקופות מפרסמות בעצמן. כל שורה נושאת קישור למקור ותאריך לכידה — טענה
          תחרותית שנשענת על שורה כזו היא <span className="font-medium">עובדה מתועדת</span>, ואותה טענה בלי
          המקור היא האשמה. אל תשתמשו במספר בלי לצרף את המקור.
        </p>
        <p className="mt-2 max-w-3xl text-xs text-slate-500">
          העמודה החשובה היא <span className="font-medium">מהטיפול ה-21</span>: שתי הקופות פותחות בהנחה
          גדולה, מגבילות אותה ל-20 טיפולים בשנה כשהייעוץ הרפואי נספר במכסה, וזה מה שהטיפול עולה אחריה.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        {funds.map((f) => (
          <div key={f.fund} className="rounded-lg border border-slate-200 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium text-slate-900">{f.fund}</span>
              <span className="text-xs text-slate-500">{f.offerings} טיפולים</span>
            </div>
            <dl className="mt-2 space-y-1 text-xs text-slate-700">
              <div>
                מכסה שנתית: <span className="font-medium">{f.annualQuota ?? "—"}</span>
                {f.quotaIncludesIntake && <span className="text-slate-500"> (כולל הייעוץ הרפואי)</span>}
              </div>
              <div>
                עליית מחיר מהטיפול ה-21:{" "}
                <span className="font-medium text-rose-700">
                  חציון +{f.medianQuotaJumpPct ?? "—"}% · מקסימום +{f.maxQuotaJumpPct ?? "—"}%
                </span>
              </div>
              <div>
                משך טיפול: {f.minDuration ?? "—"}–{f.maxDuration ?? "—"} דקות
              </div>
            </dl>
            {f.terms && <p className="mt-2 rounded bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">{f.terms}</p>}
            <div className="mt-2 text-[11px] text-slate-500">
              נלכד {f.capturedAt}{" "}
              <span className={f.captureMethod === "operator" ? "text-amber-700" : "text-slate-500"}>
                ({f.captureMethod === "operator" ? "ידני — לא מתרענן לבד" : "סריקה"})
              </span>
              {f.sourceUrl && (
                <>
                  {" · "}
                  <a href={f.sourceUrl} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                    מקור
                  </a>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {["all", ...funds.map((f) => f.fund)].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFund(f)}
            className={`rounded-full px-3 py-1 font-medium ${
              fund === f ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {f === "all" ? "הכל" : f}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-right text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">טיפול</th>
              <th className="px-3 py-2 font-medium">קופה</th>
              <th className="px-3 py-2 font-medium">משך</th>
              <th className="px-3 py-2 font-medium">מחירון</th>
              <th className="px-3 py-2 font-medium">דרג גבוה</th>
              <th className="px-3 py-2 font-medium">מהטיפול ה-21</th>
              <th className="px-3 py-2 font-medium">שינוי</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-slate-900">
                  {r.treatment}
                  {r.durationNote && <div className="text-[11px] text-slate-500">{r.durationNote}</div>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                  {r.fund.replace("קופת חולים ", "")}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-slate-600">
                  {r.durationMinutes ? `${r.durationMinutes} דק׳` : "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-slate-500">{money(r.priceList)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs font-medium tabular-nums text-emerald-700">
                  {money(r.priceTop)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs font-medium tabular-nums text-rose-700">
                  {money(r.priceAfterQuota)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">
                  {r.quotaJumpPct !== null ? (
                    <span className="rounded bg-rose-100 px-1.5 py-0.5 font-medium text-rose-700">
                      +{r.quotaJumpPct}%
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
